import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MashhadLogo from '../../../components/MashhadLogo';

const PhotographerResultCard = ({ photographer, onBookNow, onViewProfile }) => {
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US')?.format(num);
  };

  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover:border-primary/20">
      {/* Large Portfolio Image Cover */}
      <div className="relative h-72 md:h-80 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
        {photographer?.portfolioImage ? (
          <Image
            src={photographer.portfolioImage}
            alt={photographer?.portfolioImageAlt || 'صورة من معرض أعمال المصور'}
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <MashhadLogo size="xl" variant="dark" />
          </div>
        )}
        
        {/* Elegant Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-100 group-hover:opacity-90 transition-opacity duration-500"></div>
        
        {/* Bottom Gradient for Text Readability */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
        
        {/* نسبة المطابقة - أعلى يسار (مكان موثق سابقاً) */}
        <div className="absolute top-4 left-4 z-10">
          <div className="relative bg-white/95 backdrop-blur-md text-green-600 px-3 py-2 rounded-xl flex items-center gap-2 shadow-lg border border-green-200">
            <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-md">
              <Icon name="CheckCircle" size={12} className="text-green-600" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-[9px] font-medium text-green-700 leading-tight">مطابقة</span>
              <span className="text-sm font-bold leading-tight text-green-600 data-text">{formatNumber(photographer?.matchScore || 0)}%</span>
            </div>
          </div>
        </div>

        {/* المسافة (X كم) - أعلى يمين (مكان نسبة المطابقة سابقاً) */}
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-md text-slate-900 px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-semibold shadow-lg border border-white/50 z-10">
          <Icon name="Navigation" size={12} className="text-primary" />
          <span className="text-primary font-bold">
            {photographer?.distanceKm !== null && photographer?.distanceKm !== undefined
              ? (photographer.distanceLabel || `${photographer.distanceKm.toFixed(1)} كم`)
              : '-- كم'}
          </span>
        </div>

        {/* Photographer Info - Combined: Avatar at Far Right with Name & City beside it */}
        <div className="absolute bottom-4 right-2 z-10 flex items-center gap-3">
          {/* Photographer Avatar - Far Left */}
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1e40af]/30 via-[#0891b2]/30 to-[#475569]/30 backdrop-blur-sm rounded-full blur-md"></div>
            <Image
              src={photographer?.avatar || photographer?.image}
              alt={photographer?.avatarAlt || photographer?.imageAlt}
              className="relative w-20 h-20 rounded-full object-cover border-4 border-white shadow-2xl ring-2 ring-white/50"
            />
          </div>
          
          {/* Name & City - Right of Avatar */}
          <div className="text-white text-left">
            <h3 className="text-lg font-bold drop-shadow-lg line-clamp-1 mb-1">
              {photographer?.name}
            </h3>
            <div className="flex items-center gap-1.5 text-xs text-white/90 drop-shadow-md justify-start">
              <Icon name="MapPin" size={12} className="flex-shrink-0" />
              <span className="line-clamp-1">{photographer?.city}</span>
            </div>
            {/* يغطي حتى X كم مخفي حسب التصميم */}
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-5 md:p-6 bg-white">
        {/* Rating & Experience - Compact Design */}
        <div className="flex items-center justify-between gap-4 mb-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Icon name="Star" size={18} className="text-amber-400 fill-amber-400" />
              <span className="font-bold text-slate-900 text-base data-text">{formatNumber(photographer?.rating || 0)}</span>
              <span className="text-slate-500 text-sm">(<span className="data-text">{formatNumber(photographer?.reviewCount || 0)}</span>)</span>
            </div>
            <div className="w-px h-5 bg-slate-200"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-500">مستوى الخبرة:</span>
              {(() => {
                const level = photographer?.experienceLevel || photographer?.experienceLevelValue || 'معتمد';
                const isElite = level.includes('نخبة');
                const isDist  = level.includes('مميز');
                const iconName  = isElite ? 'Crown' : isDist ? 'Star' : 'BadgeCheck';
                const badgeCls  = isElite
                  ? 'bg-violet-100 text-violet-700 border-violet-300'
                  : isDist
                  ? 'bg-amber-100 text-amber-700 border-amber-300'
                  : 'bg-emerald-100 text-emerald-700 border-emerald-300';
                return (
                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${badgeCls}`}>
                    <Icon name={iconName} size={12} />
                    <span>{level}</span>
                  </div>
                );
              })()}
            </div>
          </div>
          {/* موثق في مكان متاح — متاح مخفي */}
          {photographer?.isVerified && (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
              <Icon name="BadgeCheck" size={14} className="text-primary" />
              <span>موثق</span>
            </div>
          )}
        </div>

        {/* Specialties - Modern Tags */}
        <div className="flex flex-wrap gap-2 mb-5">
          {photographer?.specialties?.slice(0, 3)?.map((specialty, index) => (
            <span
              key={index}
              className="px-3 py-1.5 bg-gradient-to-r from-primary/10 to-primary/5 text-primary text-xs font-semibold rounded-lg border border-primary/20 hover:border-primary/40 transition-colors"
            >
              {specialty}
            </span>
          ))}
          {photographer?.specialties?.length > 3 && (
            <span className="px-3 py-1.5 bg-slate-100 text-slate-500 text-xs font-semibold rounded-lg border border-slate-200">
              +{photographer.specialties.length - 3}
            </span>
          )}
          {(!photographer?.specialties || photographer?.specialties.length === 0) && (
            <span className="px-3 py-1.5 bg-slate-50 text-slate-500 text-xs font-medium rounded-lg border border-slate-200">
              مصور محترف
            </span>
          )}
        </div>

        {/* Action Buttons - Preserved */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="default"
            onClick={() => onViewProfile(photographer?.id)}
            className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-all"
            iconName="Eye"
            iconPosition="right"
          >
            عرض الملف
          </Button>
          <Button
            variant="default"
            size="default"
            onClick={() => onBookNow(photographer?.id)}
            className="flex-1 bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg transition-all"
            iconName="Calendar"
            iconPosition="right"
          >
            احجز الآن
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PhotographerResultCard;